import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { formatApiError } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  ShieldCheck, LogOut, FileText, Download, Loader2, ExternalLink, DownloadCloud, X,
} from 'lucide-react';
import { toast } from 'sonner';
import { Progress } from '../components/ui/progress';

const STATUS_CLS = {
  closed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  cancelled: 'bg-slate-100 text-slate-500 border-slate-200',
  in_progress: 'bg-amber-50 text-amber-700 border-amber-200',
  pending_approval: 'bg-violet-50 text-violet-700 border-violet-200',
  open: 'bg-red-50 text-red-700 border-red-200',
};

const today = () => new Date().toISOString().slice(0, 10);
const daysAgo = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};
// Local-time YYYY-MM-DD (avoids the UTC day-shift toISOString can cause in SAST).
const fmtLocal = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const buildPresets = () => {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  const minus = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return d; };
  return [
    { key: 'this_month', label: 'This month', start: fmtLocal(new Date(y, m, 1)), end: fmtLocal(now) },
    { key: 'last_month', label: 'Last month', start: fmtLocal(new Date(y, m - 1, 1)), end: fmtLocal(new Date(y, m, 0)) },
    { key: 'last_30', label: 'Last 30 days', start: fmtLocal(minus(30)), end: fmtLocal(now) },
    { key: 'last_90', label: 'Last 90 days', start: fmtLocal(minus(90)), end: fmtLocal(now) },
    { key: 'ytd', label: 'Year to date', start: fmtLocal(new Date(y, 0, 1)), end: fmtLocal(now) },
  ];
};

export const Portal = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [start, setStart] = useState(daysAgo(30));
  const [end, setEnd] = useState(today());
  const [downloadingId, setDownloadingId] = useState(null);
  // Gentle client-side throttle: one report at a time + a short cooldown so a
  // customer can't accidentally fire dozens of PDF builds at the server at once.
  const downloadLock = useRef(false);
  const DOWNLOAD_COOLDOWN_MS = 900;
  // Bulk "Download all" progress + cancel flag.
  const [bulk, setBulk] = useState({ running: false, done: 0, total: 0, failed: 0 });
  const bulkCancel = useRef(false);
  const presets = useMemo(buildPresets, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [meta, inc] = await Promise.all([
        api.get(`/customers/${user.customer_id}`).catch(() => ({ data: null })),
        api.get('/reports/incidents-list', { params: { start, end } }),
      ]);
      setCustomer(meta.data);
      setIncidents(inc.data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [user.customer_id, start, end]);

  useEffect(() => { load(); }, [load]);

  const kpis = useMemo(() => {
    const total = incidents.length;
    const open = incidents.filter((i) => ['open', 'in_progress', 'pending_approval'].includes(i.status)).length;
    const closed = incidents.filter((i) => i.status === 'closed').length;
    const cancelled = incidents.filter((i) => i.status === 'cancelled').length;
    return { total, open, closed, cancelled };
  }, [incidents]);

  // Core fetch + save for a single incident PDF. Returns true on success.
  const savePdf = async (incident) => {
    const r = await api.get(`/incidents/${incident.id}/report.pdf`, { responseType: 'blob' });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${incident.incident_no || incident.id}-report.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const sleep = (ms) => new Promise((res) => setTimeout(res, ms));

  const downloadIncidentPdf = async (incident) => {
    if (downloadLock.current || bulk.running) {
      toast.info('One report at a time, please — the next download will be ready in a moment.');
      return;
    }
    downloadLock.current = true;
    setDownloadingId(incident.id);
    try {
      await savePdf(incident);
    } catch (e) {
      toast.error('Could not download the incident report.');
    } finally {
      setDownloadingId(null);
      // Brief pace between downloads — keeps bulk grabbing gentle on the server.
      setTimeout(() => { downloadLock.current = false; }, DOWNLOAD_COOLDOWN_MS);
    }
  };

  const downloadAll = async () => {
    if (bulk.running || downloadLock.current) return;
    const queue = [...incidents];
    if (queue.length === 0) return;
    bulkCancel.current = false;
    setBulk({ running: true, done: 0, total: queue.length, failed: 0 });
    let done = 0;
    let failed = 0;
    for (const inc of queue) {
      if (bulkCancel.current) break;
      setDownloadingId(inc.id);
      try {
        await savePdf(inc);
      } catch (e) {
        failed += 1;
      }
      done += 1;
      setBulk((b) => ({ ...b, done, failed }));
      // Pace each download so the server is never hammered.
      await sleep(DOWNLOAD_COOLDOWN_MS);
    }
    setDownloadingId(null);
    const cancelled = bulkCancel.current;
    setBulk({ running: false, done: 0, total: 0, failed: 0 });
    if (cancelled) {
      toast.info(`Stopped — downloaded ${done} of ${queue.length} report${queue.length === 1 ? '' : 's'}.`);
    } else if (failed > 0) {
      toast.warning(`Downloaded ${done - failed} of ${queue.length}; ${failed} could not be generated.`);
    } else {
      toast.success(`All ${queue.length} report${queue.length === 1 ? '' : 's'} downloaded.`);
    }
  };

  const cancelBulk = () => { bulkCancel.current = true; };

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#0f392b] rounded-lg flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-widest text-emerald-700 font-bold" data-testid="portal-brand">
                CSG Connect · Portal
              </p>
              <h1 className="text-lg font-heading font-semibold text-slate-900" data-testid="portal-customer-name">
                {customer?.company_name || 'Loading…'}
              </h1>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right text-xs">
              <p className="text-slate-500">Signed in as</p>
              <p className="font-semibold text-slate-800" data-testid="portal-user-email">{user.email}</p>
            </div>
            <Button variant="outline" size="sm" onClick={handleLogout} data-testid="portal-logout">
              <LogOut className="w-4 h-4 mr-1.5" /> Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8 space-y-6">
        {/* KPIs */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="portal-kpis">
          {[
            { label: 'Total incidents', value: kpis.total, testid: 'kpi-total' },
            { label: 'Open / in progress', value: kpis.open, testid: 'kpi-open' },
            { label: 'Closed', value: kpis.closed, testid: 'kpi-closed' },
            { label: 'Cancelled', value: kpis.cancelled, testid: 'kpi-cancelled' },
          ].map((k) => (
            <Card key={k.testid} className="border-slate-200">
              <CardContent className="p-4">
                <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{k.label}</p>
                <p className="text-2xl font-heading font-bold text-slate-900 mt-1" data-testid={k.testid}>{k.value}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Period selector */}
        <Card className="border-slate-200">
          <CardContent className="p-4 space-y-3">
            <div className="flex flex-wrap items-center gap-2" data-testid="portal-presets">
              {presets.map((p) => {
                const active = start === p.start && end === p.end;
                return (
                  <Button
                    key={p.key}
                    size="sm"
                    variant={active ? 'default' : 'outline'}
                    className={`h-7 text-xs ${active ? 'bg-[#0f392b] hover:bg-[#0f392b]/90 text-white border-[#0f392b]' : ''}`}
                    onClick={() => { setStart(p.start); setEnd(p.end); }}
                    data-testid={`portal-preset-${p.key}`}
                  >
                    {p.label}
                  </Button>
                );
              })}
            </div>
            <div className="flex flex-wrap items-end gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">From</Label>
                <Input type="date" value={start} onChange={(e) => setStart(e.target.value)}
                       className="w-40 mt-1" data-testid="portal-start-date" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">To</Label>
                <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)}
                       className="w-40 mt-1" data-testid="portal-end-date" />
              </div>
              <div className="ml-auto text-xs text-slate-500">
                Showing {incidents.length} incident{incidents.length === 1 ? '' : 's'} in this window
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs defaultValue="incidents" className="space-y-4">
          <TabsList data-testid="portal-tabs">
            <TabsTrigger value="incidents" data-testid="portal-tab-incidents">
              <FileText className="w-4 h-4 mr-1.5" /> My Incidents
            </TabsTrigger>
          </TabsList>

          <TabsContent value="incidents">
            <Card className="border-slate-200">
              <CardHeader className="pb-3">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <CardTitle className="text-base">Incidents</CardTitle>
                  {!loading && incidents.length > 0 && (
                    <div className="flex items-center gap-2">
                      {bulk.running ? (
                        <>
                          <div className="w-40" data-testid="portal-bulk-progress">
                            <Progress value={bulk.total ? (bulk.done / bulk.total) * 100 : 0} className="h-2" />
                          </div>
                          <span className="text-xs text-slate-600 tabular-nums whitespace-nowrap" data-testid="portal-bulk-count">
                            {bulk.done} of {bulk.total} downloaded
                          </span>
                          <Button
                            size="sm" variant="outline" className="h-8 gap-1 text-xs"
                            onClick={cancelBulk}
                            data-testid="portal-cancel-all-btn"
                          >
                            <X className="w-3.5 h-3.5" /> Stop
                          </Button>
                        </>
                      ) : (
                        <Button
                          size="sm" variant="outline" className="h-8 gap-1.5 text-xs"
                          onClick={downloadAll}
                          disabled={!!downloadingId}
                          data-testid="portal-download-all-btn"
                        >
                          <DownloadCloud className="w-3.5 h-3.5" /> Download all ({incidents.length})
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="py-10 flex items-center justify-center gap-2 text-slate-500" data-testid="portal-loading">
                    <Loader2 className="w-4 h-4 animate-spin" /> Loading…
                  </div>
                ) : incidents.length === 0 ? (
                  <p className="py-10 text-center text-sm text-slate-400" data-testid="portal-empty">
                    No incidents in this window.
                  </p>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Incident #</TableHead>
                          <TableHead>Opened</TableHead>
                          <TableHead>Service</TableHead>
                          <TableHead>End Client</TableHead>
                          <TableHead>Reg</TableHead>
                          <TableHead>Contractors</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead className="text-right">Report</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {incidents.map((i, idx) => (
                          <TableRow key={i.id} data-testid={`portal-incident-row-${idx}`}>
                            <TableCell className="font-mono text-xs font-semibold">{i.incident_no}</TableCell>
                            <TableCell className="text-xs whitespace-nowrap">
                              {(i.opened_at || '').slice(0, 16).replace('T', ' ')}
                            </TableCell>
                            <TableCell className="text-xs">{i.service_name || '—'}</TableCell>
                            <TableCell className="text-xs">{i.end_client_name || '—'}</TableCell>
                            <TableCell className="text-xs font-mono">{i.vehicle_registration || '—'}</TableCell>
                            <TableCell className="text-xs max-w-[180px] truncate" title={i.contractors}>
                              {i.contractors || '—'}
                            </TableCell>
                            <TableCell>
                              <Badge className={`text-[10px] border ${STATUS_CLS[i.status] || ''}`}>
                                {(i.status || '').replace('_', ' ')}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-right">
                              <Button
                                size="sm" variant="outline" className="h-7 gap-1 text-xs"
                                disabled={!!downloadingId}
                                onClick={() => downloadIncidentPdf(i)}
                                data-testid={`portal-download-${idx}`}
                              >
                                {downloadingId === i.id
                                  ? <Loader2 className="w-3 h-3 animate-spin" />
                                  : <Download className="w-3 h-3" />} PDF
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        <p className="text-center text-xs text-slate-400 pt-4">
          Read-only portal · Data owned by CSG Connect · <ExternalLink className="inline w-3 h-3" /> csgconnect.co.za
        </p>
      </main>
    </div>
  );
};

export default Portal;
